import React from 'react';
import { CheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  PERFIL_TEMPLATES,
  PERMISSOES,
  PERMISSOES_GRUPOS,
  isPermissaoDisponivel,
  type PermissaoChave,
} from '../../types/usuarios';

/**
 * Matriz módulo × papel (handoff design_handoff_sistema) — READ-ONLY.
 * Os "papéis" são os PERFIL_TEMPLATES do front (modelos aplicáveis no
 * PermissoesEditor); permissões efetivas continuam POR USUÁRIO.
 * TODO(backend): RBAC por papel não existe — ver SISTEMA_PLANEJAMENTO.md.
 */
const MatrizPapeisPermissoes: React.FC = () => {
  const grupos = PERMISSOES_GRUPOS
    .map(g => ({ ...g, chaves: g.chaves.filter(isPermissaoDisponivel) }))
    .filter(g => g.chaves.length > 0);

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-iris border border-accent-ring bg-accent-soft px-3 py-2.5">
        <InformationCircleIcon className="h-4 w-4 text-accent-text shrink-0 mt-0.5" />
        <p className="text-[13px] text-tx">
          Estes são os <strong>modelos de perfil</strong> disponíveis como atalho. As permissões
          efetivas são definidas <strong>por usuário</strong> — clique num usuário na aba ao lado
          pra editar (os modelos podem ser aplicados lá).
        </p>
      </div>

      <div className="bg-surface border border-line rounded-iris-lg overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-line">
              <th className="sticky left-0 bg-surface text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-tx-3 min-w-[220px]">
                Permissão
              </th>
              {PERFIL_TEMPLATES.map(t => (
                <th key={t.nome} className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wide text-tx-3 whitespace-nowrap" title={t.descricao}>
                  {t.nome}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grupos.map(grupo => (
              <React.Fragment key={grupo.label}>
                <tr className="bg-surface-2">
                  <td colSpan={PERFIL_TEMPLATES.length + 1} className="sticky left-0 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-tx-2">
                    {grupo.label}
                  </td>
                </tr>
                {grupo.chaves.map(chave => (
                  <tr key={chave} className="border-b border-line last:border-b-0 hover:bg-surface-2">
                    <td className="sticky left-0 bg-surface px-4 py-1.5 text-[13px] text-tx whitespace-nowrap">
                      {PERMISSOES[chave].label}
                    </td>
                    {PERFIL_TEMPLATES.map(t => {
                      const tem = (t.permissoes as readonly PermissaoChave[]).includes(chave);
                      return (
                        <td key={t.nome} className="px-3 py-1.5 text-center">
                          <span
                            className={`inline-flex h-[22px] w-[22px] items-center justify-center rounded-iris-sm border ${
                              tem ? 'bg-accent-soft border-accent-ring' : 'bg-surface-2 border-line'
                            }`}
                            aria-label={tem ? 'Incluída no modelo' : 'Não incluída'}
                          >
                            {tem && <CheckIcon className="h-3 w-3 text-accent-text" strokeWidth={3} />}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MatrizPapeisPermissoes;
